import { useChat } from '../context/chatContext';
import { ChatMessage, EnabledPaths, Dataset } from '../types';
import { ensureDeviceToken } from '../utils/deviceToken';

export const API_BASE_URL = 'http://localhost:10000';
export const SESSION_ID_STORAGE_KEY = 'enantio-session-id';
export const SESSION_TOKEN_KEY = 'enantio-token';

let inFlightRefresh: Promise<any> | null = null;

const guardedRefresh = async (...args: Parameters<typeof refreshSession>) => {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshSession(...args).finally(() => { inFlightRefresh = null; });
  }
  return inFlightRefresh;
};

const refreshSession = async (
  userId?: string,
  token?: string,
  setSessionId?: (userId: string) => void,
  setToken?: (token: string) => void
) => {
  try {
    const deviceToken = await ensureDeviceToken();
    const res = await fetch(`${API_BASE_URL}/api/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(deviceToken ? { 'X-Device-Token': deviceToken } : {}),
      }, // <- no Authorization
      body: JSON.stringify({ user_id: userId, token }), // send both if your API expects them
    });
    if (!res.ok) return null;

    const data = await res.json();

    if (data?.token) {
      localStorage.setItem(SESSION_TOKEN_KEY, data.token);
      setToken?.(data.token);
    }
    if (data?.user_id) {
      localStorage.setItem(SESSION_ID_STORAGE_KEY, data.user_id);
      setSessionId?.(data.user_id);
    }
    return data;
  } catch {
    return null; // <- IMPORTANT: do not return the error object
  }
};


const fetchApi: (
  endpoint: string,
  options: RequestInit,
  userId?: string,
  setSessionId?: (userId: string) => void,
  setToken?: (token: string) => void,
  retry?: boolean
) => any = async (
  endpoint: string,
  options: RequestInit = {},
  userId: string = '',
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {},
  retry: boolean = true
) => {
  const token = localStorage.getItem(SESSION_TOKEN_KEY) || undefined;
  const deviceToken = await ensureDeviceToken();
  const headers: HeadersInit = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(deviceToken ? { 'X-Device-Token': deviceToken } : {}),
    ...(options.headers || {}),
  };

  const resp = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  // OK path
  if (resp.ok) {
    const ct = resp.headers.get('content-type') || '';
    return ct.includes('application/json') ? resp.json() : resp.text();
  }

  console.log('here too')
  // Only try to refresh on auth errors; never for 404/500 etc.
  if ((resp.status === 401 || resp.status === 403) && retry) {
    console.log('not here')
    // inside fetchApi on 401/403:
    const refreshed = await guardedRefresh(userId, token, setSessionId, setToken);
    if (refreshed) {
      console.log('refreshed')
      // Retry once with updated token from localStorage
      return await fetchApi(endpoint, options, refreshed.user_id ?? userId, setSessionId, setToken, false);
    }
  }

  // Build error object
  let errorData: any = { detail: `HTTP error! status: ${resp.status}` };
  try {
    errorData = await resp.json();
  } catch { /* ignore */ }
  const err: any = new Error(errorData.detail || 'An unknown error occurred');
  err.status = resp.status;
  throw err;
};

/**
 * Sends a query (message and optional file) to the backend.
 */
export const sendQuery = async (
  message: string,
  thoughtLimit: number,
  files: File[],
  userId?: string | null,
  chatId?: string,
  clientMessageId?: string,
  setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {},
  deepLearning: boolean = false,
): Promise<{ message: ChatMessage; userId: string, userMessageId: string }> => {
  const formData = new FormData();
  formData.append('query', message);
  if (userId) {
    formData.append('user_id', userId);
  }
  if (chatId) {
    formData.append('chat_id', chatId);
  }
  if(clientMessageId) {
    formData.append('client_msg_id', clientMessageId);
  }
  
  formData.append('deep_learning', `${deepLearning}`);

  if(thoughtLimit > 2) {
    formData.append('max_steps', thoughtLimit.toString());

  }

  if (files.length) {
    files.forEach(file => formData.append('files', file));
  }
  const responseData = await fetchApi('/api/chats/query', {
    method: 'POST',
    body: formData,
    headers: { 'X-Idempotency-Key': clientMessageId || '' },
  }, userId || undefined, setSessionId, setToken);
  return {
    message: {
      id: responseData.message_id,
      client_message_id: responseData.client_msg_id,
      sender: 'enantio',
      text: responseData.response,
    },
    userId: responseData.user_id,
    userMessageId: responseData.user_message_id
  };
};
/**
 * Fetches the list of all available datasets from the backend.
 */
export const listAllDatasets = async (
  userId: string,
  orgDatasets: boolean,
  setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}
): Promise<{ datasets: Dataset[] }> => {
  const params = new URLSearchParams({
    user_id: userId,
    org_datasets: String(orgDatasets)
  });
  return await fetchApi(`/api/datasets/list?${params.toString()}`, {
    method: 'GET'
  }, userId || undefined, setSessionId, setToken);
};
/**
 * Fetches current cognitive path and dataset settings.
 * We'll assume this endpoint returns all available datasets as well.
 */
export const getSettings = async (userId: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi(`/api/settings/get?user_id=${userId}`, {
    method: 'GET',
  }, userId || undefined, setSessionId, setToken);
};

/**
 * Updates cognitive path flags for the current session.
 */
export const updatePaths = async (userId: string, paths: { enabled_paths: EnabledPaths }, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/settings/update', {
    method: 'POST',
    body: JSON.stringify({user_id: userId, enabled_paths: paths.enabled_paths }),
  }, userId || undefined, setSessionId, setToken);
};

export const updateShowAds = async (userId: string, showAds: boolean, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/settings/update', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, show_ads: showAds }),
  }, userId || undefined, setSessionId, setToken);
};

/**
 * Updates the active datasets for the current session.
 */
export const selectDatasets = async (userId: string, datasets: { active_datasets: string[] }, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/datasets/select', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, ...datasets }),
  }, userId || undefined, setSessionId, setToken);
};

/**
 * Retrieves thought usage and tier for the current session.
 */
export const getUsage = async (userId: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  const data = await fetchApi(`/api/usage?user_id=${userId}`, {
    method: 'GET',
  }, userId || undefined, setSessionId, setToken);
  return { ...data, showAds: data.show_ads };
};

export const updateSubscription = async (userId: string, tier: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/subscription/update', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, tier }),
  }, userId || undefined, setSessionId, setToken);
};

/**
 * Retrieves session.
 */
export const getSession = async (userId?: string, token?: string) => {
  const data = await refreshSession(userId, token);
  if (!data) {
    const err: any = new Error('Failed to establish session');
    err.status = 401;
    throw err;
  }
  return data;
};
/**
 * Retrieves stored chat history for a session.
 */
export const getChatHistory = async (
  userId: string,
  offset = 0,
  limit = 100,
  setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}
) => {
  return await fetchApi(
    `/api/chat/history?user_id=${userId}&offset=${offset}&limit=${limit}`,
    {
      method: 'GET',
    }, userId || undefined, setSessionId, setToken
  );
};

export const pinChat = async (
  userId: string,
  chatId: string,
  pin: boolean,
  setSessionId: (userId: string) => void = (userId: string) => {},
  setToken: (token: string) => void = (token: string) => {}
) => {
  return await fetchApi(
    '/api/chats/pin',
    {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, chat_id: chatId, pin }),
    },
    userId || undefined,
    setSessionId,
    setToken
  );
};

export const deleteChat = async (
  userId: string,
  chatId: string,
  setSessionId: (userId: string) => void = (userId: string) => {},
  setToken: (token: string) => void = (token: string) => {}
) => {
  return await fetchApi(
    '/api/chats/delete',
    {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, chat_id: chatId }),
    },
    userId || undefined,
    setSessionId,
    setToken
  );
};

export const assignLicense = async (userId: string, email: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/licenses/assign', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, email })
  }, userId || undefined, setSessionId, setToken);
};

export const removeLicense = async (userId: string, email: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/licenses/remove', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, email })
  }, userId || undefined, setSessionId, setToken);
};

export const listLicenses = async (userId: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi(`/api/licenses/list?user_id=${userId}`, {
    method: 'GET'
  }, userId || undefined, setSessionId, setToken);
};

export const registerGoogle = async (userId: string, email: string, google_id_token: string, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi(`/api/auth/google?google_id_token=${google_id_token}`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, email })
  }, userId || undefined, setSessionId, setToken);
};

export const saveOrganizationDetails = async (userId: string, organizationName: string, quantity: number, setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}) => {
  return await fetchApi('/api/organization/save', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, organization_name: organizationName, quantity }),
  }, userId || undefined, setSessionId, setToken);
};

export const getChatMessages = async (
  chatId: string,
  userId: string,
  token: string,
  start = 0,
  end = 10,
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {}
): Promise<{
  chat_id: string;
  total: number;
  start: number;
  end: number;
  messages: ChatMessage[];
}> => {
  const params = new URLSearchParams({
    user_id: userId,
    token: token,
    start: String(start),
    end: String(end),
  });

  const data = await fetchApi(
    `/api/chats/${encodeURIComponent(chatId)}/messages?${params.toString()}`,
    { method: 'GET' },
    userId,
    setSessionId,
    setToken
  );
  // Normalize id fields for deduplication
  data.messages = (data.messages || []).map((m: any) => ({
    ...m,
    id: m.message_id ?? m.id,
    client_message_id: m.client_msg_id ?? m.client_message_id,
  }));
  return data;
};

export const searchChats = async (
  userId: string,
  query: string,
  start = 0,
  end = 20,
  chatId: string | undefined = undefined,
  deep = false,
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {}
): Promise<{
  query: string;
  deep: boolean;
  auto_escalated: boolean;
  plan: string;
  chat_id?: string;
  total: number;
  start: number;
  end: number;
  results: any[];
}> => {
  const params = new URLSearchParams({
    user_id: userId,
    q: query,
    start: String(start),
    end: String(end),
    deep: deep ? '1' : '0',
  });
  if (chatId) {
    params.append('chat_id', chatId);
  }

  return await fetchApi(
    `/api/chats/search?${params.toString()}`,
    { method: 'GET' },
    userId,
    setSessionId,
    setToken
  );
};

export const suggestTitle = async (
  chatId: string,
  maxChars = 48,
  userId?: string,
  setSessionId: (userId: string) => void = (userId: string) => {},
  setToken: (token: string) => void = (token: string) => {}
) => {
  return await fetchApi(
    `/api/chats/${chatId}/title/suggest`,
    {
      method: 'POST',
      body: JSON.stringify({ max_chars: maxChars }),
    },
    userId || undefined,
    setSessionId,
    setToken
  );
};

// --- NEW: create a backend file-processing job (dataset or personality) ---
export const createFileJob = async (
  userId: string,
  body: { kind: 'dataset' | 'personality'; name: string; orgDatasets?: boolean },
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {}
): Promise<{ job_id: string }> => {
  // backend expects snake_case for org flag
  const payload = { user_id: userId, kind: body.kind, name: body.name, org_datasets: !!body.orgDatasets };
  return await fetchApi(
    '/api/jobs/file/start',
    { method: 'POST', body: JSON.stringify(payload) },
    userId || undefined,
    setSessionId,
    setToken
  );
};

// --- NEW: poll job status ---
export const getJobStatus = async (
  userId: string,
  jobId: string,
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {}
): Promise<{ status: 'queued'|'uploading'|'processing'|'completed'|'failed'; progress?: number; detail?: string }> => {
  const params = new URLSearchParams({ user_id: userId });
  return await fetchApi(
    `/api/jobs/${encodeURIComponent(jobId)}?${params.toString()}`,
    { method: 'GET' },
    userId || undefined,
    setSessionId,
    setToken
  );
};


// REPLACE your current uploadDataset with this version (keeps old usage working)
export const uploadDataset = async (
  name: string,
  files: File[],
  userId: string | null | undefined,
  orgDatasets = false,
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {},
  opts?: { jobId?: string; onProgress?: (pct: number) => void } // <-- new optional arg
) => {
  const formData = new FormData();
  formData.append('name', name);
  if (userId) formData.append('user_id', userId);
  formData.append('org_datasets', String(!!orgDatasets));
  if (opts?.jobId) formData.append('job_id', opts.jobId);
  files.forEach(file => formData.append('files', file));

  // Use XHR to report upload progress if available
  const token = localStorage.getItem(SESSION_TOKEN_KEY) || undefined;
  const url = `${API_BASE_URL}/api/storage/upload`;

  const canUseXHR = typeof window !== 'undefined' && typeof XMLHttpRequest !== 'undefined';
  if (canUseXHR && opts?.onProgress) {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && opts.onProgress) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          opts.onProgress(Math.max(0, Math.min(100, pct)));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
    return;
  }

  // Fallback: no progress (uses fetchApi for auth/refresh)
  return await fetchApi(
    '/api/storage/upload',
    { method: 'POST', body: formData },
    userId || undefined,
    setSessionId,
    setToken
  );
};


export const getStorageItems = async (
  type: 'events' | 'impressions' | 'schemas',
  sessionId?: string,
  datasetName?: string,
  setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}
) => {
  const params = new URLSearchParams();
  if (sessionId) params.append('session_id', sessionId);
  if (datasetName) params.append('name', datasetName);
  return await fetchApi(`/api/storage/${type}?${params.toString()}`, {
    method: 'GET',
  }, sessionId || undefined, setSessionId, setToken);
};

export const deleteStorageItem = async (
  userId: string,
  itemId: string,
  itemType: 'event' | 'impression' | 'schema',
  datasetName?: string,
  setSessionId: (userId: string) => void = () => {},
  setToken: (token: string) => void = () => {}
) => {
  return await fetchApi(
    '/api/storage/delete',
    { method: 'POST', body: JSON.stringify({ user_id: userId, item_id: itemId, item_type: itemType, name: datasetName || '' }) },
    userId,
    setSessionId,
    setToken
  );
};

export const getNotes = async (
  userId: string,
  agent: string,
  setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}
): Promise<{ user_note: string; org_note: string }> => {
  const params = new URLSearchParams({
    user_id: userId,
    agent: agent,
  });
  return await fetchApi(`/api/agents/notes?${params.toString()}`, {
    method: 'GET',
  }, userId || undefined, setSessionId, setToken);
};


export const refineNotes = async (
  userId: string,
  agent: string,
  setSessionId: (userId: string) => void = (userId: string) => {}, setToken: (token: string) => void = (token: string) => {}
): Promise<{ user_note: string; org_note: string }> => {
  const params = new URLSearchParams({
    user_id: userId,
    agent: agent,
  });
  return await fetchApi(`/api/agents/refine?`, {
    method: 'POST',
    body: JSON.stringify({
      user_id: userId,
      agent: agent,
    })
  }, userId || undefined, setSessionId, setToken);
};


export const listPersonalities = async (
  userId: string,
  setSessionId: (userId: string) => void = () => {}, 
  setToken: (token: string) => void = () => {}
): Promise<{ files: string[] }> => {
  const params = new URLSearchParams({ user_id: userId });
  return await fetchApi(`/api/personality/list?${params.toString()}`, { method: 'GET' }, userId, setSessionId, setToken);
};

/**
 * Retrieves the content of a specific personality file.
 */
export const getPersonalityFile = async (
  userId: string,
  name: string,
  setSessionId: (userId: string) => void = () => {}, 
  setToken: (token: string) => void = () => {}
): Promise<{ content: string }> => {
  const params = new URLSearchParams({ user_id: userId, name });
  return await fetchApi(`/api/personality/get?${params.toString()}`, { method: 'GET' }, userId, setSessionId, setToken);
};

/**
 * Updates the content of an existing personality file.
 */
export const updatePersonalityFile = async (
  userId: string,
  name: string,
  content: string,
  setSessionId: (userId: string) => void = () => {}, 
  setToken: (token: string) => void = () => {}
) => {
  return await fetchApi('/api/personality/put', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, name, content })
  }, userId, setSessionId, setToken);
};

/**
 * Uploads one or more files to create or update a personality profile.
 * This matches the backend endpoint that accepts multiple files and metadata.
 */
export const uploadPersonalityFile = async (
  name: string,
  files: File[],
  userId: string,
  orgPersonality: boolean,
  setSessionId: (userId: string) => void = () => {}, 
  setToken: (token: string) => void = () => {}
) => {
  const formData = new FormData();
  formData.append('user_id', userId);
  formData.append('name', name);
  formData.append('org_personality', String(orgPersonality));

  files.forEach(file => {
    formData.append('files', file);
  });

  // Note: We don't set Content-Type header here. 
  // The browser will automatically set it to 'multipart/form-data' with the correct boundary.
  return await fetchApi('/api/personality/upload', { 
    method: 'POST', 
    body: formData 
  }, userId, setSessionId, setToken);
};


/**
 * Deletes a personality profile directory and all its contents.
 */
export const deletePersonalityFile = async (
  userId: string,
  personalityName: string,
  setSessionId: (userId: string) => void = () => {}, 
  setToken: (token: string) => void = () => {}
) => {
    // The backend endpoint seems to expect a POST request for deletion based on frontend code.
    // A more RESTful approach would be DELETE, but we follow the existing pattern.
    // The URL structure is also based on the frontend component's usage.
  return await fetchApi(`/api/personality/delete`, { 
      method: 'DELETE',
      body: JSON.stringify({
        user_id: userId,
        name: personalityName
      })
    }, userId, setSessionId, setToken);
};